import { usePrevious } from '@react-hookz/web';
import type { UpdateBlockStateType } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { ComputeContextType } from '@unifyapps/carbon/no-code/hooks/computeContext/BuildComputeContext';
import { useDataSourceResponses } from '@unifyapps/carbon/no-code/hooks/useDataSourceResponses';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { isNil } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { getIsDefaultValueDataSourceLoading } from '../../../dataSources/utils/getIsDefaultValueDataSourceLoading';
import { getResolvedDefaultValue } from '../utils/getResolvedDefaultValue';

interface UseSetBooleanBlockDefaultValueProps<T extends { value?: boolean | string }> {
  defaultValue?: unknown;
  updateBlockState: UpdateBlockStateType<T>;
  dataSourceIds: string[] | undefined;
  getGlobalStoreContext: () => ComputeContextType;
  value?: boolean | string;
  uncomputedDefaultValue?: string;
}

export function useSetDefaultValue<T extends { value?: boolean }>({
  defaultValue,
  updateBlockState,
  dataSourceIds,
  getGlobalStoreContext,
  value,
  uncomputedDefaultValue,
}: UseSetBooleanBlockDefaultValueProps<T>) {
  const context = getGlobalStoreContext();
  const { dataSourceResponses } = useDataSourceResponses(context, dataSourceIds);
  const interfaceMode = useInterfaceStore().use.mode();
  const isMounting = useRef(true);

  const resolvedDefaultValue = useMemo(() => getResolvedDefaultValue(defaultValue), [defaultValue]);

  // Determines if resolvedDefaultValue is a boolean (true/false) or null/undefined.
  // This boolean check serves two purposes:
  // 1. Acts as a null-check - if non-boolean, no default value should be set
  // 2. Ensures type safety - only actual boolean values will initialize the component
  // In Builder mode, this also controls reset behavior when default value changes
  const isResolvedDefaultValueBoolean = typeof resolvedDefaultValue === 'boolean';

  const isLoadingDefaultValue = useMemo(
    () =>
      getIsDefaultValueDataSourceLoading({
        dataSourceResponses,
        dataSourceIds,
        defaultValue: uncomputedDefaultValue,
      }),
    [dataSourceIds, dataSourceResponses, uncomputedDefaultValue],
  );

  const previousLoadingDefaultValue = usePrevious(isLoadingDefaultValue);
  const shouldInitializeValue = !isLoadingDefaultValue && previousLoadingDefaultValue;

  useEffect(() => {
    if (shouldInitializeValue && isNil(value) && isResolvedDefaultValueBoolean) {
      // if the default value is coming from data source,
      // we wait for data source to load and then set the value of checkbox/toggle to the default value
      updateBlockState((draft) => {
        draft.value = resolvedDefaultValue;
      });
    } else if (isMounting.current && isResolvedDefaultValueBoolean) {
      updateBlockState((draft) => {
        draft.value = resolvedDefaultValue;
      });
    }
    isMounting.current = false;
  }, [
    isResolvedDefaultValueBoolean,
    resolvedDefaultValue,
    shouldInitializeValue,
    updateBlockState,
    value,
  ]);

  useEffect(() => {
    if (interfaceMode === InterfaceModes.BUILDER && isResolvedDefaultValueBoolean) {
      // in  builder mode, we reset the checkbox/toggle value to the default when the default value is changed.
      updateBlockState((draft) => {
        draft.value = resolvedDefaultValue;
      });
    }
  }, [isResolvedDefaultValueBoolean, resolvedDefaultValue, interfaceMode, updateBlockState]);

  return {
    resolvedValue: typeof value === 'boolean' ? value : false,
  };
}
